import { DivConst } from 'constants/divConst';

import type { useApiState } from 'hooks/useApiState';
import type { AcctStateType } from 'stores/atoms/acctState';
import type { VoiceChatResponseType } from 'types/api/voiceChatResponseType';
import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';
import { getProductionUrl } from 'utils/terms';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

type UseApiStateReturn = ReturnType<typeof useApiState>;

/**
 * postVoiceChatRegister の引数型から companyId / voiceChatStatusDiv を除いた共通パラメータ型。
 * postVoiceChatUpdate へのスプレッドにも利用する。
 */
export type VoiceChatCommonParam = Omit<
  Parameters<UseApiStateReturn['postVoiceChatRegister']>[0],
  'companyId' | 'voiceChatStatusDiv'
>;

// ------------------------------------------------------------------ //
// convertVoiceChatValuesToParam
// ------------------------------------------------------------------ //

/**
 * select / radio 由来の文字列値を number | null に型強制する。
 * HTML フォームは select の value を常に文字列で返すため、API スキーマ（number | null）に揃える。
 */
function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  return Number(value);
}

/**
 * react-hook-form のフォーム値を API 送信パラメータに変換する。
 * - popupImageKey は含まない（hook 側で別途セットする）
 * - noIndexFlag は含まない（reqSchema に存在しないフィールドのため送らない）
 * - guidanceFlag が false の場合は guidanceText を null にする（保存しない）
 * - 無音待ち秒（UI）は ×1000 して silenceMs（ms）に変換する
 * - completeXxx（単一）は results[0]（配列・長1）に変換する
 * - explainOnly（説明のみ）設問は requiredFlag=false / supplementFlag=false / supplementPlaceholder=null に強制する
 */
export function convertVoiceChatValuesToParam(
  values: VoiceChatEditFormStateType
): VoiceChatCommonParam {
  const { voiceChatQuestionMode } = DivConst.items;

  return {
    title: values.title ?? null,
    titleHiddenFlag: values.titleHiddenFlag ?? false,
    description: values.description ?? null,
    respondentMailDiv:
      values.respondentMailDiv ?? DivConst.items.voiceChatRespondentMail.none.value,
    respondentFields: values.respondentFields.map(field => ({
      getFlag: field.getFlag,
      respondentFieldDiv: toNumberOrNull(field.respondentFieldDiv),
      respondentStandardFieldDiv: toNumberOrNull(field.respondentStandardFieldDiv),
      label: field.label ?? null,
      placeholder: field.placeholder ?? null,
      respondentCustomFieldDiv: toNumberOrNull(field.respondentCustomFieldDiv),
      respondentCustomFieldInputDiv: toNumberOrNull(field.respondentCustomFieldInputDiv),
      maxLength: toNumberOrNull(field.maxLength),
    })),
    imageKey: values.imageKey ?? null,
    designDiv: values.voiceChatTemplateId ?? null,
    consentFlag: values.consentFlag ?? false,
    guidanceFlag: values.guidanceFlag ?? false,
    guidanceTextDisplayFlag: values.guidanceTextDisplayFlag ?? true,
    answerConfirmButtonFlag: values.answerConfirmButtonFlag ?? true,
    answerAutoStartFlag: values.answerAutoStartFlag ?? false,
    guidanceText: values.guidanceFlag ? (values.guidanceText ?? null) : null,
    guidanceSpeechText: values.guidanceSpeechText || null,
    multilingualFlag: values.multilingualFlag ?? false,
    voiceDiv: values.voiceDiv ?? DivConst.items.voiceChatVoice.female.value,
    silenceMs: (values.silenceWaitSec ?? 0) * 1000,
    maxAnswerSec: values.maxRecordSec ?? 0,
    benchmarkContactListId: values.benchmarkContactListId ?? null,
    gtmId: values.gtmId ?? null,
    trackingParamFlag: values.trackingParamFlag ?? false,
    publishStartAt: values.publishStartAt ?? null,
    publishEndAt: values.publishEndAt ?? null,
    results: [
      {
        title: values.completeTitle ?? null,
        descriptionHtml: values.completeDescriptionHtml ?? null,
        imageKey: values.completeImageKey ?? null,
      },
    ],
    questions: values.questions.map(question => {
      const isExplainAndAnswer =
        Number(question.answerModeDiv) === voiceChatQuestionMode.explainAndAnswer.value;

      return {
        questionModeDiv: Number(
          question.answerModeDiv ?? voiceChatQuestionMode.explainAndAnswer.value
        ),
        title: question.title ?? null,
        speechText: question.speechText || null,
        requiredFlag: isExplainAndAnswer ? (question.requiredFlag ?? false) : false,
        supplementFlag: isExplainAndAnswer ? (question.supplementFlag ?? false) : false,
        supplementPlaceholder:
          isExplainAndAnswer && question.supplementFlag
            ? (question.supplementPlaceholder ?? null)
            : null,
        titleDisplayFlag: question.titleDisplayFlag ?? true,
      };
    }),
  };
}

// ------------------------------------------------------------------ //
// buildInitialVoiceChatForm
// ------------------------------------------------------------------ //

/**
 * 新規ボイスチャット作成時のフォーム初期値を生成する。
 * モック VcVoiceChatEdit.tsx の既定値（同意ON・ガイダンスON・女性音声・無音2秒・最大録音120秒・デザイン1）に忠実。
 */
export function buildInitialVoiceChatForm(
  acct: AcctStateType
): Partial<VoiceChatEditFormStateType> {
  const company = acct.company;
  const { termsInputType, voiceChatRespondentMail, voiceChatVoice } = DivConst.items;

  const termsUrl =
    company.termsInputDiv === termsInputType.url.value
      ? (company.termsUrl ?? '')
      : getProductionUrl(company.cd);

  return {
    privacyPolicyUrl: company.privacyPolicyUrl ?? '',
    termsUrl,
    copyright: company.copyright ?? '',
    publishStartAt: undefined,
    publishEndAt: undefined,
    noIndexFlag: false,
    voiceChatTemplateId: 1,
    respondentMailDiv: voiceChatRespondentMail.beginning.value,
    respondentFieldGroupId: undefined,
    respondentFields: [],
    popupImageKey: undefined,
    consentFlag: true,
    guidanceFlag: true,
    guidanceTextDisplayFlag: true,
    answerConfirmButtonFlag: true,
    // DB 既定値は false（既存コンテンツの挙動を変えないため）。新規作成時のみ true にして
    // 「説明後に自動で録音を開始する」を既定 ON にする（設計メモ参照）
    answerAutoStartFlag: true,
    multilingualFlag: false,
    trackingParamFlag: false,
    guidanceText: 'この後、5問のアンケートにお答えください。それでは最初の質問です。',
    guidanceSpeechText: '',
    voiceDiv: voiceChatVoice.female.value,
    silenceWaitSec: 2,
    maxRecordSec: 120,
    questions: [
      {
        answerModeDiv: DivConst.items.voiceChatQuestionMode.explainAndAnswer.value,
        title: '',
        speechText: '',
        requiredFlag: true,
        supplementFlag: false,
        supplementPlaceholder: '補足があれば入力（任意）',
        titleDisplayFlag: true,
      },
    ],
  };
}

// ------------------------------------------------------------------ //
// buildEditFormFromResponse
// ------------------------------------------------------------------ //

/**
 * API レスポンスをフォーム state に変換する（編集画面初期化用）。
 * - silenceMs（ms）は ÷1000 して silenceWaitSec（UI 秒）に変換する
 * - results[0]（配列）は completeXxx（単一）に変換する
 */
export function buildEditFormFromResponse(
  response: VoiceChatResponseType
): Partial<VoiceChatEditFormStateType> {
  const result = response.results[0];

  return {
    title: response.title ?? undefined,
    titleHiddenFlag: response.titleHiddenFlag,
    description: response.description ?? undefined,
    respondentMailDiv: response.respondentMailDiv ?? undefined,
    respondentFieldGroupId: response.respondentFieldGroupId ?? undefined,
    imageKey: response.imageKey ?? undefined,
    popupImageKey: response.popupImageKey ?? undefined,
    respondentFields: response.respondentFields.map(field => ({
      ...field,
    })) as unknown as VoiceChatEditFormStateType['respondentFields'],
    voiceChatTemplateId: response.designDiv ?? undefined,
    consentFlag: response.consentFlag,
    guidanceFlag: response.guidanceFlag,
    guidanceTextDisplayFlag: response.guidanceTextDisplayFlag,
    answerConfirmButtonFlag: response.answerConfirmButtonFlag,
    answerAutoStartFlag: response.answerAutoStartFlag,
    guidanceText: response.guidanceText ?? undefined,
    guidanceSpeechText: response.guidanceSpeechText ?? undefined,
    multilingualFlag: response.multilingualFlag,
    voiceDiv: response.voiceDiv ?? undefined,
    silenceWaitSec: response.silenceMs != null ? response.silenceMs / 1000 : undefined,
    maxRecordSec: response.maxAnswerSec ?? undefined,
    completeTitle: result?.title ?? undefined,
    completeDescriptionHtml: result?.descriptionHtml ?? undefined,
    completeImageKey: result?.imageKey ?? undefined,
    benchmarkContactListId: response.benchmarkContactListId ?? undefined,
    gtmId: response.gtmId ?? undefined,
    trackingParamFlag: response.trackingParamFlag,
    publishStartAt: response.publishStartAt ?? undefined,
    publishEndAt: response.publishEndAt ?? undefined,
    noIndexFlag: response.noIndexFlag,
    questions: response.questions.map(q => ({
      answerModeDiv: q.questionModeDiv,
      title: q.title ?? undefined,
      speechText: q.speechText ?? undefined,
      requiredFlag: q.requiredFlag,
      supplementFlag: q.supplementFlag,
      supplementPlaceholder: q.supplementPlaceholder ?? undefined,
      titleDisplayFlag: q.titleDisplayFlag,
    })),
  };
}
